<template>
  <div class="mobile-community-posts-add">
    <div class="add-container">

      <!-- 表单内容 -->
      <div class="form-content">
        <!-- 所属社区 -->
        <van-cell-group>
          <van-field
            v-model="form.deptName"
            is-link
            readonly
            required
            label="所属社区"
            placeholder="请选择社区"
            @click="openDeptPicker"
          />
          <van-popup 
            v-model="showDeptPicker" 
            position="bottom" 
            get-container="body"
            :style="{ height: '55vh' }"
          >
            <div class="dept-picker-wrapper">
              <div class="picker-header">
                <span class="picker-title">选择所属社区</span>
                <span class="picker-close" @click="showDeptPicker = false">×</span>
              </div>
              <div class="picker-columns">
                <!-- 左列：街道列表（第一级） -->
                <div class="picker-column picker-column-left">
                  <div 
                    v-for="item in deptParentList" 
                    :key="item.value"
                    class="picker-item"
                    :class="{ active: selectedParent === item.value }"
                    @click="selectParent(item)"
                  >
                    {{ item.text }}
                    <span v-if="item.children && item.children.length > 0" class="item-count">
                      ({{ item.children.length }})
                    </span>
                  </div>
                  <div v-if="deptParentList.length === 0" class="picker-empty">
                    暂无数据
                  </div>
                </div>
                <!-- 右列：社区列表（第二级） -->
                <div class="picker-column picker-column-right">
                  <div 
                    v-for="item in deptChildList" 
                    :key="item.value"
                    class="picker-item"
                    :class="{ active: selectedChild === item.value }"
                    @click="selectChild(item)"
                  >
                    {{ item.text }}
                  </div>
                  <div v-if="deptChildList.length === 0" class="picker-empty">
                    {{ selectedParent ? '该街道暂无社区' : '请选择街道' }}
                  </div>
                </div>
              </div>
              <div class="picker-footer">
                <div class="picker-tip">
                  <span v-if="selectedParent && !selectedChild" class="tip-text">
                    已选择：{{ getSelectedParentName() }}
                  </span>
                  <span v-if="selectedChild" class="tip-text">
                    已选择：{{ getSelectedChildName() }}
                  </span>
                  <span v-if="!selectedParent && !selectedChild" class="tip-text">
                    请选择街道或社区
                  </span>
                </div>
                <div class="footer-buttons">
                  <van-button plain size="small" @click="showDeptPicker = false">取消</van-button>
                  <van-button 
                    type="primary" 
                    size="small" 
                    @click="confirmDept"
                    :disabled="!selectedParent && !selectedChild"
                  >
                    确认
                  </van-button>
                </div>
              </div>
            </div>
          </van-popup>
        </van-cell-group>

        <!-- 所属园区 -->
        <van-cell-group>
          <van-field
            v-model="form.zoneParkName"
            is-link
            readonly
            required
            label="所属园区"
            placeholder="请选择园区"
            @click="showParkPicker = true"
          />
          <van-popup v-model="showParkPicker" position="bottom" get-container="body">
            <van-picker
              show-toolbar
              :columns="parkOptions"
              value-key="name"
              @cancel="showParkPicker = false"
              @confirm="onParkConfirm"
            />
          </van-popup>
        </van-cell-group>

        <!-- 标题 -->
        <van-cell-group>
          <van-field
            v-model="form.title"
            required
            label="标题"
            placeholder="请输入标题"
            maxlength="50"
          />
        </van-cell-group>

        <!-- 发布时间 -->
        <van-cell-group>
          <van-field
            v-model="form.publishTime"
            is-link
            readonly
            required
            label="发布时间"
            placeholder="请选择发布时间"
            @click="showPublishTimePicker = true"
          />
          <van-popup v-model="showPublishTimePicker" position="bottom" get-container="body">
            <van-datetime-picker
              v-model="currentDate"
              type="datetime"
              title="请选择发布时间"
              @cancel="showPublishTimePicker = false"
              @confirm="onPublishTimeConfirm"
            />
          </van-popup>
        </van-cell-group>

        <!-- 封面 -->
        <van-cell-group>
          <div class="upload-wrapper">
            <div class="upload-label">封面 <span class="required-mark">*</span></div>
            <van-uploader
              v-model="fileList"
              :max-count="1"
              :after-read="afterRead"
              @delete="handleDelete"
            >
              <van-button icon="plus" type="primary" size="small">上传封面</van-button>
            </van-uploader>
            <!-- 上传进度/状态提示 -->
            <div v-if="uploadStatus" class="upload-status">
              <van-loading v-if="uploadStatus === 'uploading'" size="16" />
              <span :class="uploadStatus === 'success' ? 'status-success' : 'status-error'">
                {{ uploadStatusText }}
              </span>
            </div>
            <!-- 预览图（用于回显） -->
            <div v-if="form.cover && !fileList.length" class="upload-preview">
              <img :src="form.cover" alt="封面预览" />
              <div class="preview-actions">
                <van-icon name="delete" class="delete-icon" @click="removeCover" />
              </div>
            </div>
          </div>
        </van-cell-group>

        <!-- 内容 - 使用富文本编辑器 -->
        <div class="editor-wrapper">
          <div class="editor-label">内容 <span class="required-mark">*</span></div>
           <Editor :value.sync="form.content" placeholder="请输入描述内容" />
        </div>
      </div>

      <!-- 提交按钮 -->
      <div class="submit-wrapper">
        <van-button
          type="info"
          size="large"
          round
          :loading="submitting"
          @click="submitForm"
        >
          {{ submitting ? '提交中...' : (form.id ? '修 改' : '发 布') }}
        </van-button>
      </div>
    </div>
  </div>
</template>

<script>
import { Toast } from 'vant';
import axios from 'axios';
// 如果 config 导入有问题，可以直接使用相对路径或环境变量
// import config from '@/config';
import config from '@/config/changan'
import { 
  listZoneCommunityPosts, 
  getZoneCommunityPosts, 
  addZoneCommunityPosts, 
  updateZoneCommunityPosts 
} from "@/api/Changan-zhsq/zoneCommunityPosts";
import { listZonePark, deptTreeSelect } from "@/api/Changan-zhsq/businessInfo";
import Editor from "@/components/Editor";
import { setwgToken } from '@/utils/auth';

export default {
  name: 'MobileCommunityPostsAdd',
  components: {
    Editor
  },
  data() {
    return {
      title: '新增公告',
      submitting: false,
      currentDate: new Date(),
      
      // 封面相关
      fileList: [],
      coverFile: null,
      uploadStatus: '',
      uploadStatusText: '',
      
      // 社区相关
      showDeptPicker: false,
      deptParentList: [],
      deptChildList: [],
      selectedParent: null,
      selectedChild: null,
      
      // 园区相关
      showParkPicker: false,
      parkOptions: [],
      
      // 时间选择器
      showPublishTimePicker: false,
      
      // 表单数据
      form: {
        id: null,
        deptId: null,
        deptName: '',
        zoneParkId: null,
        zoneParkName: '',
        title: '',
        content: '',
        cover: '',
        publishTime: '',
        notifyType: null,
      }
    };
  },
  created() {
    const data = this.$route.query.data;
    if (data) {
      try {
        const row = JSON.parse(data);
        this.form = {
          ...this.form,
          ...row,
        };
        this.title = '修改公告';
      } catch (e) {
        console.error('解析数据失败', e);
      }
    }
    setwgToken(this.$route.query.tok);
    this.getDeptTree();
    this.getParkList();
  },
  methods: {
    /** 获取社区树 */
    getDeptTree() {
      deptTreeSelect().then(response => {
        const data = response.data.data || [];
        this.deptParentList = data.map(item => ({
          ...item,
          text: item.OrganName || item.organName,
          value: item.OrganCode || item.organCode,
          children: (item.children || []).map(child => ({
            ...child,
            text: child.OrganName || child.organName,
            value: child.OrganCode || child.organCode
          }))
        }));
        
        if (this.form.deptId) {
          this.initDeptSelection();
        }
      }).catch(() => {
      });
    },
    
    /** 初始化选择 */
    initDeptSelection() {
      const parent = this.deptParentList.find(item => item.value === this.form.deptId);
      if (parent) {
        this.selectedParent = parent.value;
        this.selectedChild = null;
        this.deptChildList = parent.children || [];
        return;
      }
      
      for (const p of this.deptParentList) {
        const child = (p.children || []).find(c => c.value === this.form.deptId);
        if (child) {
          this.selectedParent = p.value;
          this.selectedChild = child.value;
          this.deptChildList = p.children || [];
          return;
        }
      }
    },
    
    /** 打开社区选择器 */
    openDeptPicker() {
      if (this.selectedParent) {
        const parent = this.deptParentList.find(item => item.value === this.selectedParent);
        if (parent) {
          this.deptChildList = parent.children || [];
        }
      }
      this.showDeptPicker = true;
    },
    
    /** 选择街道 */
    selectParent(item) {
      if (this.selectedParent === item.value) {
        this.selectedParent = null;
        this.selectedChild = null;
        this.deptChildList = [];
      } else {
        this.selectedParent = item.value;
        this.selectedChild = null;
        this.deptChildList = item.children || [];
      }
    },
    
    /** 选择社区 */
    selectChild(item) {
      if (this.selectedChild === item.value) {
        this.selectedChild = null;
      } else {
        this.selectedChild = item.value;
        if (!this.selectedParent) {
          for (const p of this.deptParentList) {
            if ((p.children || []).some(c => c.value === item.value)) {
              this.selectedParent = p.value;
              this.deptChildList = p.children || [];
              break;
            }
          }
        }
      }
    },
    
    /** 获取选中的街道名称 */
    getSelectedParentName() {
      const parent = this.deptParentList.find(item => item.value === this.selectedParent);
      return parent ? parent.text : '';
    },
    
    /** 获取选中的社区名称 */
    getSelectedChildName() {
      if (!this.selectedChild) return '';
      for (const p of this.deptParentList) {
        const child = (p.children || []).find(c => c.value === this.selectedChild);
        if (child) return child.text;
      }
      return '';
    },
    
    /** 确认选择 */
    confirmDept() {
      if (!this.selectedParent && !this.selectedChild) {
        Toast.fail('请选择街道或社区');
        return;
      }
      
      if (this.selectedChild) {
        const child = this.deptChildList.find(item => item.value === this.selectedChild);
        if (child) {
          this.form.deptId = child.value;
          this.form.deptName = child.text;
        }
      } else if (this.selectedParent) {
        const parent = this.deptParentList.find(item => item.value === this.selectedParent);
        if (parent) {
          this.form.deptId = parent.value;
          this.form.deptName = parent.text;
        }
      }
      
      this.showDeptPicker = false;
    },
    
    /** 获取园区列表 */
    getParkList() {
      listZonePark().then(response => {
        this.parkOptions = response.data.data || [];
      }).catch(() => {
      });
    },
    
    /** 园区确认 */
    onParkConfirm(value) {
      this.form.zoneParkId = value.id;
      this.form.zoneParkName = value.name;
      this.showParkPicker = false;
    },
    
    /** 发布时间确认 */
    onPublishTimeConfirm(value) {
      this.form.publishTime = this.formatDateTime(value);
      this.showPublishTimePicker = false;
    },
    
    /** 格式化日期时间 */
    formatDateTime(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    },
    
    /**
     * ============================================================
     * 优化后的封面上传逻辑（修复 protocol 错误）
     * ============================================================
     */
    
    /** 上传封面 - 使用更健壮的方式 */
    afterRead(file) {
      // 保存文件对象
      this.coverFile = file.file;
      
      // 显示上传中状态
      this.uploadStatus = 'uploading';
      this.uploadStatusText = '上传中...';
      
      // 创建 FormData
      const formData = new FormData();
      formData.append('file', file.file);
      formData.append('fileName', file.file.name);
      
      // 如果有 openId 需要传递，从路由或 store 获取
      // const openId = this.$route.query.openId || '';
      // if (openId) {
      //   formData.append('openId', openId);
      // }
      
      // ============================================================
      // 方法1：使用项目中已有的上传接口
      // 如果项目中有封装好的上传 API，直接使用
      // ============================================================
      
      // 假设项目中有 uploadFile 的 API 方法
      // import { uploadFile } from '@/api/upload';
      // uploadFile(formData).then(res => { ... })
      
      // ============================================================
      // 方法2：使用 axios 直接上传（推荐，更可控）
      // ============================================================
      
      // 获取上传地址 - 多种方式兼容
      let uploadUrl = '';
      uploadUrl = config.apiRequestUrl + '/common/OssUploadImg'; // 请替换为实际的上传接口
      
      // 如果 uploadUrl 为空，给出提示
      if (!uploadUrl) {
        this.uploadStatus = 'error';
        this.uploadStatusText = '上传地址未配置';
        Toast.fail('上传地址未配置，请联系管理员');
        this.fileList = [];
        this.form.cover = '';
        return;
      }
      
      // 配置请求
      const configHeaders = {
        headers: { 
          'Content-Type': 'multipart/form-data',
          // 如果需要 token，从 store 获取
          // 'Authorization': 'Bearer ' + getToken()
        },
        timeout: 30000 // 30秒超时
      };
      
      // 发送请求
      axios.post(uploadUrl, formData, configHeaders)
        .then(res => {
          // 检查响应状态
          if (res.status === 200 && res.data) {
            const data = res.data;
            
            // 根据实际接口返回结构调整
            // 常见返回格式：
            // 1. { code: 0, data: 'url', msg: 'success' }
            // 2. { code: 200, data: { url: 'xxx' } }
            // 3. { success: true, url: 'xxx' }
            
            let imageUrl = '';
            
            if (data.code === 0 || data.code === 200) {
              imageUrl = data.data || data.url || data.msg || '';
            } else if (data.success) {
              imageUrl = data.url || data.data || '';
            } else if (typeof data === 'string') {
              imageUrl = data;
            } else {
              // 尝试从常见字段获取
              imageUrl = data.url || data.data || data.msg || data.fileUrl || '';
            }
            
            if (imageUrl) {
              // 上传成功
              this.uploadStatus = 'success';
              this.uploadStatusText = '上传成功';
              this.form.cover = imageUrl;
              
              // 更新 fileList 显示上传成功的图片
              this.fileList = [{
                url: imageUrl,
                isImage: true
              }];
              
              Toast.success('上传成功');
            } else {
              // 响应中无图片地址
              this.uploadStatus = 'error';
              this.uploadStatusText = '上传失败：未获取到图片地址';
              Toast.fail('上传失败，请重试');
              this.fileList = [];
              this.form.cover = '';
            }
          } else {
            // 响应状态异常
            this.uploadStatus = 'error';
            this.uploadStatusText = `上传失败：${res.status}`;
            Toast.fail(`上传失败（${res.status}），请重试`);
            this.fileList = [];
            this.form.cover = '';
          }
        })
        .catch(err => {
          console.error('上传失败:', err);
          
          let errorMsg = '上传失败，请重试';
          if (err.response) {
            // 服务器返回了错误状态码
            errorMsg = err.response.data?.msg || err.response.data?.message || `上传失败（${err.response.status}）`;
          } else if (err.request) {
            // 请求已发送但未收到响应
            errorMsg = '网络异常，请检查网络连接';
          } else {
            // 请求配置出错
            errorMsg = err.message || '上传失败，请重试';
          }
          
          this.uploadStatus = 'error';
          this.uploadStatusText = errorMsg;
          Toast.fail(errorMsg);
          this.fileList = [];
          this.form.cover = '';
        });
    },
    
    /** 删除封面 */
    handleDelete(file, detail) {
      this.form.cover = '';
      this.fileList = [];
      this.uploadStatus = '';
      this.uploadStatusText = '';
      this.coverFile = null;
      Toast('已删除封面');
    },
    
    /** 手动移除封面 */
    removeCover() {
      this.form.cover = '';
      this.fileList = [];
      this.uploadStatus = '';
      this.uploadStatusText = '';
      this.coverFile = null;
    },
    
    // ============================================================
    // 表单验证和提交
    // ============================================================
    
    /** 表单验证 */
    validateForm() {
      if (!this.form.deptId) {
        Toast.fail('请选择所属社区');
        return false;
      }
      if (!this.form.zoneParkId) {
        Toast.fail('请选择所属园区');
        return false;
      }
      if (!this.form.title || !this.form.title.trim()) {
        Toast.fail('请输入标题');
        return false;
      }
      if (!this.form.publishTime) {
        Toast.fail('请选择发布时间');
        return false;
      }
      if (!this.form.cover) {
        Toast.fail('请上传封面');
        return false;
      }
      if (!this.form.content || !this.form.content.trim()) {
        Toast.fail('请输入内容');
        return false;
      }
      return true;
    },
    
    /** 提交表单 */
    submitForm() {
      if (!this.validateForm()) {
        return;
      }
      
      this.submitting = true;
      
      const submitData = {
        ...this.form,
      };
      delete submitData.zoneParkName;
      
      const apiCall = this.form.id 
        ? updateZoneCommunityPosts(submitData) 
        : addZoneCommunityPosts(submitData);
      
      apiCall.then(() => {
        Toast.success(this.form.id ? '修改成功' : '发布成功');
        this.submitting = false;
        setTimeout(() => {
          this.$router.go(-1);
        }, 3000);
      }).catch((err) => {
        this.submitting = false;
        Toast.fail(err.msg || '操作失败，请重试');
      });
    }
  }
};
</script>

<style scoped>
.mobile-community-posts-add {
  min-height: 100vh;
  background: #f5f7fa;
  padding: 12px 16px 100px;
  width: calc(100% - 32px);
}

.add-container {
  max-width: 480px;
  margin: 0 auto;
}

.page-header {
  padding: 16px 0 20px;
  text-align: center;
}

.header-title {
  font-size: 20px;
  font-weight: 600;
  color: #333;
}

.form-content {
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
}

/* 覆盖 Vant 样式 */
.van-cell-group {
  margin: 0;
}

.van-cell {
  padding: 14px 16px;
  font-size: 14px;
}

.van-cell::after {
  border-bottom: 1px solid #f0f0f0;
}

.van-field__label {
  width: 80px;
  color: #333;
}

.van-field__body {
  color: #666;
}

.van-field__control {
  text-align: right;
}

.van-field__control::placeholder {
  color: #bbb;
}

.van-field__right-icon {
  color: #bbb;
}

/* ========== 上传组件样式（优化） ========== */
.upload-wrapper {
  padding: 14px 16px;
  border-bottom: 1px solid #f0f0f0;
}

.upload-label {
  font-size: 14px;
  color: #333;
  margin-bottom: 10px;
}

.required-mark {
  color: #ee0a24;
  margin-left: 2px;
}

/* 上传状态提示 */
.upload-status {
  margin-top: 8px;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
}

.upload-status .status-success {
  color: #07c160;
}

.upload-status .status-error {
  color: #ee0a24;
}

/* 预览区域（用于回显已有封面） */
.upload-preview {
  margin-top: 10px;
  position: relative;
  display: inline-block;
}

.upload-preview img {
  width: 100px;
  height: 66px;
  object-fit: cover;
  border-radius: 4px;
  border: 1px solid #e8e8e8;
}

.preview-actions {
  position: absolute;
  top: -8px;
  right: -8px;
  background: rgba(0, 0, 0, 0.6);
  border-radius: 50%;
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.preview-actions .delete-icon {
  color: #fff;
  font-size: 14px;
}

/* ========== 原有样式 ========== */

/* 编辑器样式 */
.editor-wrapper {
  padding: 12px 16px 20px;
  background: #fff;
}

.editor-label {
  padding-bottom: 10px;
  font-size: 14px;
  color: #333;
}

/* 提交按钮 */
.submit-wrapper {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 16px 20px 20px;
  background: #fff;
  box-shadow: 0 -2px 12px rgba(0, 0, 0, 0.06);
  z-index: 100;
}

.submit-wrapper .van-button {
  background: linear-gradient(135deg, #409EFF, #116efa);
  border: none;
  font-size: 16px;
}

.submit-wrapper .van-button--large {
  height: 48px;
}

/* 两列联动选择器样式 */
.dept-picker-wrapper {
  background: #fff;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.picker-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #f0f0f0;
  flex-shrink: 0;
}

.picker-title {
  font-size: 16px;
  font-weight: 500;
  color: #333;
}

.picker-close {
  font-size: 24px;
  color: #999;
  cursor: pointer;
  line-height: 1;
  padding: 0 4px;
}

.picker-columns {
  flex: 1;
  display: flex;
  overflow: hidden;
  min-height: 0;
}

.picker-column {
  flex: 1;
  overflow-y: auto;
  padding: 8px 0;
  -webkit-overflow-scrolling: touch;
}

.picker-column-left {
  border-right: 1px solid #f0f0f0;
  background: #fafafa;
}

.picker-column-right {
  background: #fff;
}

.picker-item {
  padding: 14px 16px;
  text-align: center;
  font-size: 14px;
  color: #333;
  cursor: pointer;
  transition: all 0.2s;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
}

.picker-item:active {
  background: #f5f5f5;
}

.picker-item.active {
  color: #409EFF;
  background: #f0f7ff;
  font-weight: 500;
}

.picker-item.active::after {
  content: '✓';
  position: absolute;
  right: 16px;
  color: #409EFF;
  font-weight: 600;
  font-size: 16px;
}

.item-count {
  font-size: 12px;
  color: #999;
  font-weight: 400;
}

.picker-empty {
  padding: 40px 16px;
  text-align: center;
  font-size: 14px;
  color: #bbb;
}

.picker-footer {
  padding: 12px 16px;
  border-top: 1px solid #f0f0f0;
  flex-shrink: 0;
  background: #fff;
}

.picker-tip {
  text-align: center;
  margin-bottom: 10px;
  min-height: 20px;
}

.tip-text {
  font-size: 14px;
  color: #666;
}

.tip-text:not(:empty)::before {
  content: '当前选择: ';
  color: #999;
}

.footer-buttons {
  display: flex;
  gap: 12px;
}

.footer-buttons .van-button {
  flex: 1;
  height: 40px;
  border-radius: 20px;
}

.footer-buttons .van-button--primary {
  background: linear-gradient(135deg, #409EFF, #116efa);
  border: none;
}

.footer-buttons .van-button--primary[disabled] {
  background: #ccc;
  opacity: 0.6;
}

/* Picker 弹出层样式 */
.van-picker__toolbar {
  padding: 12px 16px;
}

.van-picker__cancel,
.van-picker__confirm {
  font-size: 14px;
}

.van-picker__title {
  font-size: 16px;
  font-weight: 500;
}
</style>